import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../../context/useAuth';
import { useSocket } from '../../../context/useSocket';
import { fetchConversations } from '../api/chatApi';

// Koliko razgovora seed-ujemo pri startu (za tacan zbir nep+rocitanih pre nego
// sto stigne bilo koji live event). Zavisni razgovori se ionako azuriraju uzivo.
const SEED_LIMIT = 50;

const ChatNotificationsContext = createContext({
  totalUnread: 0,
  unreadConversations: 0,
  markConversationRead: () => {},
  setActiveConversation: () => {},
  refreshUnread: () => {},
});

// Globalno prati nepročitane poruke apoteke da bi badge mogao da se prikaze na
// "Poruke" tabu čak i kada admin nije na toj stranici. Seed preko REST-a, a
// zatim uživo preko `conversation:updated` (isti izvor kao lista razgovora).
export const ChatNotificationsProvider = ({ children }) => {
  const { token } = useAuth();
  const { socket } = useSocket();

  // conversationId -> pharmacyUnreadCount (drzimo samo one > 0).
  const [unreadMap, setUnreadMap] = useState({});

  // Razgovor koji je trenutno otvoren (aktivan chat). Dok se apoteka aktivno
  // dopisuje sa nekim, njegove nove poruke se ne broje kao neprocitane. Drzimo
  // ga u ref-u da socket handler cita svezu vrednost bez ponovnog pretplacivanja.
  const activeConversationIdRef = useRef(null);
  const setActiveConversation = useCallback((conversationId) => {
    activeConversationIdRef.current = conversationId || null;
    // Cim postane aktivan, ocisti eventualno zabelezeno neprocitano za njega.
    if (conversationId) {
      setUnreadMap((prev) => {
        if (prev[conversationId] == null) return prev;
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
    }
  }, []);

  // Poslednji pokrenuti seed; stariji odgovori se odbacuju (npr. refreshUnread
  // pozvan dok pocetni seed jos traje).
  const latestSeedRef = useRef(0);

  const seedUnread = useCallback(async () => {
    const seedId = latestSeedRef.current + 1;
    latestSeedRef.current = seedId;

    const { data, ok } = await fetchConversations({
      page: 1,
      limit: SEED_LIMIT,
    });
    if (seedId !== latestSeedRef.current || !ok || !data) return;

    const items = Array.isArray(data.items) ? data.items : [];
    const map = {};
    items.forEach((conversation) => {
      if (conversation?.id && conversation.pharmacyUnreadCount > 0) {
        map[conversation.id] = conversation.pharmacyUnreadCount;
      }
    });
    setUnreadMap(map);
  }, []);

  // Seed kada postoji token (posle logina); reset na logout.
  useEffect(() => {
    if (!token) {
      // Odbaci seed koji je eventualno u toku.
      latestSeedRef.current += 1;
      setUnreadMap({});
      return;
    }

    seedUnread();
  }, [seedUnread, token]);

  // Ponovni seed na zahtev - npr. posle trajnog brisanja pacijenta, cime nestaje i
  // njegov razgovor, a bek za to ne salje socket event, pa bi njegove neprocitane
  // poruke ostale u broju na "Poruke" tabu.
  const refreshUnread = useCallback(() => {
    if (token) seedUnread();
  }, [seedUnread, token]);

  // Uživo: svaki `conversation:updated` nosi tacan `pharmacyUnreadCount`.
  useEffect(() => {
    if (!socket) return undefined;

    const handleUpdated = (conversation) => {
      if (!conversation?.id) return;
      setUnreadMap((prev) => {
        // Aktivan (otvoren) razgovor se ne broji kao neprocitan - korisnik ga
        // upravo cita. Ako je vec zabelezen, ukloni ga.
        const isActive = conversation.id === activeConversationIdRef.current;
        const count = isActive ? 0 : conversation.pharmacyUnreadCount || 0;
        if (count > 0) {
          if (prev[conversation.id] === count) return prev;
          return { ...prev, [conversation.id]: count };
        }
        if (prev[conversation.id] == null) return prev;
        const next = { ...prev };
        delete next[conversation.id];
        return next;
      });
    };

    socket.on('conversation:updated', handleUpdated);
    return () => socket.off('conversation:updated', handleUpdated);
  }, [socket]);

  // Lokalno očisti kada apoteka otvori razgovor - server emituje
  // `conversation:updated` ostalim nalozima, ali ne nuzno i citaocu.
  const markConversationRead = useCallback((conversationId) => {
    setUnreadMap((prev) => {
      if (prev[conversationId] == null) return prev;
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
  }, []);

  const value = useMemo(() => {
    const counts = Object.values(unreadMap);
    return {
      // Zbir svih nepročitanih poruka (za broj na badge-u).
      totalUnread: counts.reduce((sum, count) => sum + count, 0),
      // Broj razgovora sa nepročitanim (za alternativni prikaz / tackicu).
      unreadConversations: counts.length,
      markConversationRead,
      setActiveConversation,
      refreshUnread,
    };
  }, [unreadMap, markConversationRead, setActiveConversation, refreshUnread]);

  return (
    <ChatNotificationsContext.Provider value={value}>
      {children}
    </ChatNotificationsContext.Provider>
  );
};

ChatNotificationsProvider.propTypes = {
  children: PropTypes.node,
};

export const useChatNotifications = () => useContext(ChatNotificationsContext);
